import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useParams } from 'react-router-dom';
import {
  addDoc,
  collection,
  getDoc,
  serverTimestamp,
  updateDoc,
  doc,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { hashToken } from '@/lib/inviteToken';
import { CULTURE_BLOCKS, CULTURE_QUESTIONS, type CultureBlock } from '@/config/cultureQuestions';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { cn } from '@/lib/utils';

type PageStatus = 'loading' | 'invalid' | 'completed' | 'ready' | 'saving' | 'done';

interface InvitePayload {
  id: string;
  companyId: string;
  employeeId?: string;
  department?: string;
  status?: string;
  campaignId?: string;
}

const QUESTION_BY_ID = Object.fromEntries(CULTURE_QUESTIONS.map((q) => [q.id, q]));

function emptyAnswers(): Record<string, number> {
  const o: Record<string, number> = {};
  for (const q of CULTURE_QUESTIONS) {
    o[q.id] = 0;
  }
  return o;
}

function sumBlock(answers: Record<string, number>, itemIds: readonly string[]): number {
  return itemIds.reduce((acc, id) => acc + (Number(answers[id]) || 0), 0);
}

function blockTotalsValid(answers: Record<string, number>): boolean {
  return CULTURE_BLOCKS.every((block) => Math.round(sumBlock(answers, block.items)) === 100);
}

export function CultureAssessmentPage() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<PageStatus>('loading');
  const [invite, setInvite] = useState<InvitePayload | null>(null);
  const [actualAnswers, setActualAnswers] = useState<Record<string, number>>(emptyAnswers);
  const [desiredAnswers, setDesiredAnswers] = useState<Record<string, number>>(emptyAnswers);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    async function validateInvite() {
      if (!token) {
        setStatus('invalid');
        return;
      }

      try {
        const tokenHash = await hashToken(token);
        const lookupSnap = await getDoc(doc(db, 'culture_invite_token_lookup', tokenHash));
        if (!lookupSnap.exists()) {
          setStatus('invalid');
          return;
        }
        const lookupData = lookupSnap.data() as Record<string, unknown>;
        const inviteId = lookupData.inviteId as string | undefined;
        if (!inviteId) {
          setStatus('invalid');
          return;
        }

        const inviteDoc = await getDoc(doc(db, 'culture_invites', inviteId));
        if (!inviteDoc.exists()) {
          setStatus('invalid');
          return;
        }
        const data = inviteDoc.data() as Record<string, unknown>;
        if (data.status === 'completed') {
          setStatus('completed');
          return;
        }

        setInvite({
          id: inviteDoc.id,
          companyId: (data.companyId as string) ?? '',
          employeeId: data.employeeId as string | undefined,
          department: data.department as string | undefined,
          status: data.status as string | undefined,
          campaignId: typeof data.campaignId === 'string' ? data.campaignId : undefined,
        });
        setActualAnswers(emptyAnswers());
        setDesiredAnswers(emptyAnswers());
        setStatus('ready');
      } catch (error) {
        console.error('[CultureAssessment] Error validando invitación:', error);
        setStatus('invalid');
      }
    }

    void validateInvite();
  }, [token]);

  const actualBlocksValid = useMemo(() => blockTotalsValid(actualAnswers), [actualAnswers]);
  const desiredBlocksValid = useMemo(() => blockTotalsValid(desiredAnswers), [desiredAnswers]);
  const allBlocksValid = actualBlocksValid && desiredBlocksValid;

  async function handleSubmit() {
    if (!invite || !allBlocksValid) return;

    setSubmitError(null);
    setStatus('saving');

    try {
      await addDoc(collection(db, 'culture_assessments'), {
        inviteId: invite.id,
        companyId: invite.companyId,
        employeeId: invite.employeeId ?? null,
        department: invite.department ?? 'No especificado',
        actualAnswers,
        desiredAnswers,
        status: 'completed',
        completedAt: serverTimestamp(),
        ...(invite.campaignId ? { campaignId: invite.campaignId } : {}),
      });

      await updateDoc(doc(db, 'culture_invites', invite.id), {
        status: 'completed',
        completedAt: serverTimestamp(),
      });

      setStatus('done');
    } catch (error) {
      console.error('[CultureAssessment] Error guardando respuestas:', error);
      setSubmitError('No se pudieron guardar tus respuestas. Intenta nuevamente.');
      setStatus('ready');
    }
  }

  if (status === 'loading' || status === 'saving') {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <p className="text-muted-foreground">
          {status === 'saving' ? 'Guardando respuestas...' : 'Validando invitación...'}
        </p>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="text-center">
          <p className="font-medium text-destructive">Enlace inválido o expirado</p>
          <p className="mt-2 text-sm text-muted-foreground">
            No fue posible validar esta invitación de Cultura Organizacional.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'completed' || status === 'done') {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 py-10">
        <Card className="w-full max-w-xl">
          <CardHeader>
            <CardTitle>Gracias por responder</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Tu evaluación de Cultura Organizacional ya fue registrada correctamente.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 px-4 py-8 pb-24">
      <div>
        <h1 className="text-2xl font-semibold">Cultura Organizacional</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Para cada bloque tienes <strong>100 puntos</strong>. Repártelos entre las cuatro afirmaciones según cómo es{' '}
          <strong>HOY</strong> en la empresa y cómo <strong>DEBERÍA SER</strong> para tener éxito. En cada bloque, la
          suma de cada columna (Actual y Deseada) debe ser exactamente 100.
        </p>
      </div>

      {submitError && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>
      )}

      <div className="space-y-8">
        {CULTURE_BLOCKS.map((block) => (
          <Card key={block.id}>
            <CardHeader>
              <CardTitle className="text-lg">{block.title}</CardTitle>
              <CardDescription>
                Reparte 100 puntos entre las cuatro afirmaciones (0–100 cada una; la suma del bloque debe ser 100).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <BlockColumn
                label="Cultura actual (¿cómo es HOY?)"
                block={block}
                answers={actualAnswers}
                setAnswers={setActualAnswers}
              />
              <BlockColumn
                label="Cultura deseada (¿cómo DEBERÍA ser?)"
                block={block}
                answers={desiredAnswers}
                setAnswers={setDesiredAnswers}
              />
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="sticky bottom-0 -mx-4 border-t bg-background/95 px-4 py-4 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="mx-auto flex max-w-4xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            {!allBlocksValid
              ? 'Completa los 5 bloques: en cada uno, Actual y Deseada deben sumar 100 puntos.'
              : 'Listo para enviar tu evaluación.'}
          </p>
          <Button type="button" onClick={() => void handleSubmit()} disabled={!allBlocksValid || status === 'saving'}>
            Finalizar evaluación
          </Button>
        </div>
      </div>
    </div>
  );
}

function BlockColumn({
  label,
  block,
  answers,
  setAnswers,
}: {
  label: string;
  block: CultureBlock;
  answers: Record<string, number>;
  setAnswers: Dispatch<SetStateAction<Record<string, number>>>;
}) {
  const total = sumBlock(answers, block.items);
  const rounded = Math.round(total);
  const ok = rounded === 100;

  return (
    <div className="space-y-3 rounded-lg border bg-muted/20 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium">{label}</p>
        <p
          className={cn(
            'text-sm font-semibold tabular-nums',
            ok ? 'text-green-600 dark:text-green-400' : 'text-destructive'
          )}
        >
          Total: {rounded} / 100 {ok ? '✓' : ''}
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {block.items.map((itemId) => {
          const q = QUESTION_BY_ID[itemId];
          if (!q) return null;
          return (
            <div key={itemId} className="space-y-2">
              <Label htmlFor={`${block.id}-${itemId}`} className="text-xs leading-snug text-muted-foreground">
                {q.text}
              </Label>
              <Input
                id={`${block.id}-${itemId}`}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                spellCheck={false}
                value={String(answers[itemId] ?? 0)}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '');
                  const n = digits === '' ? 0 : Math.min(100, parseInt(digits, 10));
                  setAnswers((prev) => ({
                    ...prev,
                    [itemId]: Number.isFinite(n) ? n : 0,
                  }));
                }}
                className="tabular-nums"
                placeholder="0–100"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
